import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import Select from "../../../../../components/common/Select/Select";
import { USER_ROLE, DOCUMENT_CONSTANTS } from "Constants/constant";
import { apiGetRequest } from "helpers/Requests";
import { roleAccess } from "helpers/RolesPermission";
import { apiCall } from "store/services/service";
import axios from "store/services/axios";
import { BASE_URL } from "store/services/URL";
import {
  getUrlRole,
  isValidFile,
  maxFileSize,
  getFileExtension,
  classNames,
} from "Utils/utils";
import { getUser } from "../../../../../store/selector/auth.selector";
import FileIcon from "components/common/FileIcon/FileIcon";

/** Participant docs: visibility false = shown to worker / visible in Frontline lists. */
const getDefaultParticipantDocVisibility = (role) => {
  if (role !== "participant") return true;
  return roleAccess([USER_ROLE.Sp_Standard_User]) ? false : true;
};

const UPLOAD_STATUS = {
  PENDING: "pending",
  UPLOADING: "uploading",
  READY: "ready",
  REGISTERING: "registering",
  SUCCESS: "success",
  FAILED: "failed",
};

const getFileBaseName = (fileName) => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot <= 0) return fileName;
  return fileName.substring(0, lastDot);
};

const BulkUploadDocumentModal = (props) => {
  const {
    role,
    handleClose,
    isOpen,
    workerId,
    parentId,
    currentLoginUser,
    isUserGracePeriodOver,
    onUploadComplete,
    silHomeDocumentUploadFileRequest,
  } = props;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [isDocTypeLoader, setIsDocTypeLoader] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [visibility, setVisibility] = useState(() =>
    getDefaultParticipantDocVisibility(role)
  );
  const [selectionLimitError, setSelectionLimitError] = useState("");
  const uploadingRef = useRef(new Set());
  const bulkSessionIdRef = useRef(null);

  const isS3Uploading = selectedFiles.some(
    (f) =>
      f.status === UPLOAD_STATUS.PENDING || f.status === UPLOAD_STATUS.UPLOADING
  );

  const readyFiles = selectedFiles.filter((f) => f.status === UPLOAD_STATUS.READY);
  const hasReadyFiles = readyFiles.length > 0;

  useEffect(() => {
    if (isOpen) {
      bulkSessionIdRef.current = `bulk-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 12)}`;
      getFileTypes();
      if (role === "sil") {
        setSelectedFileType({ label: "Sil Documents", value: 2 });
      }
      if (role === "participant") {
        setVisibility(getDefaultParticipantDocVisibility(role));
      }
    } else {
      handleClear();
    }
  }, [isOpen, role]);

  const getFileTypes = async () => {
    try {
      setIsDocTypeLoader(true);
      const res = await apiGetRequest(`${getUrlRole()}/doc-types`);
      if (res.status === 200) {
        const options = res.data.doc_types.map((doc) => ({
          label: doc.name,
          value: doc.id,
        }));
        setFileTypeOptions([...options]);
      }
      setIsDocTypeLoader(false);
    } catch (err) {
      console.log("err", err);
      setIsDocTypeLoader(false);
    }
  };

  const handleClear = () => {
    uploadingRef.current.clear();
    bulkSessionIdRef.current = null;
    setSelectedFiles([]);
    setVisibility(getDefaultParticipantDocVisibility(role));
    setIsRegistering(false);
    setSelectionLimitError("");
    if (role !== "sil") {
      setSelectedFileType(null);
    }
  };

  const updateFileEntry = (id, updates) => {
    setSelectedFiles((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const uploadFileToS3 = (file, fileId) => {
    return new Promise((resolve, reject) => {
      const contentType = file.type || "application/octet-stream";
      apiCall
        .post({
          url: `${BASE_URL}signed-url`,
          params: { contentType },
          isAuthToken: currentLoginUser?.AccessToken,
        })
        .then((response) => {
          const headers = { ...response.headers };
          if ("Host" in headers) {
            delete headers.Host;
          }
          axios
            .put(response.url, file, {
              headers,
              onUploadProgress: (progressEvent) => {
                const progress = progressEvent.total
                  ? (progressEvent.loaded / progressEvent.total) * 100
                  : 0;
                updateFileEntry(fileId, { progress });
              },
            })
            .then(() => {
              resolve({
                key: response.key,
                ext: file.name.split(".").pop()?.toLowerCase() || "",
              });
            })
            .catch(reject);
        })
        .catch(reject);
    });
  };

  const startS3Upload = useCallback(
    async (fileItem) => {
      if (uploadingRef.current.has(fileItem.id)) return;
      uploadingRef.current.add(fileItem.id);
      updateFileEntry(fileItem.id, {
        status: UPLOAD_STATUS.UPLOADING,
        progress: 0,
        errorMessage: null,
      });

      try {
        const s3Data = await uploadFileToS3(fileItem.file, fileItem.id);
        updateFileEntry(fileItem.id, {
          status: UPLOAD_STATUS.READY,
          progress: 100,
          s3Data,
          errorMessage: null,
        });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to upload file to storage";
        updateFileEntry(fileItem.id, {
          status: UPLOAD_STATUS.FAILED,
          progress: 0,
          errorMessage,
        });
      } finally {
        uploadingRef.current.delete(fileItem.id);
      }
    },
    [currentLoginUser?.AccessToken]
  );

  const buildUploadPayload = (file, s3Data) => {
    const baseName = getFileBaseName(file.name);
    const payload = {
      name: baseName,
      file_type: s3Data.ext,
      key: s3Data.key,
      document_type_id: selectedFileType?.value,
      with_expiry: false,
      label: "",
      note: "xyz",
      expiry_time: null,
      folder_structure_id: parentId,
      visibility: visibility,
      bulk_session_id: bulkSessionIdRef.current || undefined,
    };

    if (role === "sil") {
      Object.assign(payload, { sil_home_id: workerId });
    } else if (role === "participant") {
      Object.assign(payload, {
        pt_id: workerId,
        worker_id: workerId,
        visibility,
      });
    } else {
      Object.assign(payload, { worker_id: workerId });
    }

    return payload;
  };

  const registerDocument = (payload) => {
    if (role === "sil") {
      return new Promise((resolve, reject) => {
        // skipToast: bulk modal shows one summary toast after all files finish
        silHomeDocumentUploadFileRequest(
          { ...payload, skipToast: true },
          (response, error) => {
            if (error) {
              reject(
                error instanceof Error
                  ? error
                  : new Error("Failed to upload document")
              );
              return;
            }
            resolve(response);
          }
        );
      });
    }

    const Role = roleAccess([USER_ROLE.Sp_Standard_User]) ? "hrm" : "sp";
    const URL = `${BASE_URL + Role + "/file-management/upload-document"}`;
    const URLNew = `${
      BASE_URL + `${Role}/file-management/participant/upload-document`
    }`;

    return apiCall.post({
      url: role === "participant" ? URLNew : URL,
      params: payload,
      isAuthToken: currentLoginUser?.AccessToken,
    });
  };

  const handleRegisterDocuments = async () => {
    if (isUserGracePeriodOver || isRegistering || isS3Uploading) return;

    if (role !== "sil" && !selectedFileType) {
      toast.error("Please select a document category");
      return;
    }

    if (!hasReadyFiles) {
      toast.error("Please wait for files to finish uploading before continuing");
      return;
    }

    const maxBulkFiles = DOCUMENT_CONSTANTS.MAX_BULK_UPLOAD_FILES;
    if (selectedFiles.length > maxBulkFiles || readyFiles.length > maxBulkFiles) {
      const message = `Maximum ${maxBulkFiles} documents allowed per bulk upload. Remove extra files and try again.`;
      setSelectionLimitError(message);
      toast.error(message);
      return;
    }

    setIsRegistering(true);
    let successCount = 0;
    let failCount = 0;

    for (const fileItem of readyFiles) {
      updateFileEntry(fileItem.id, { status: UPLOAD_STATUS.REGISTERING, progress: 0 });
      try {
        const payload = buildUploadPayload(fileItem.file, fileItem.s3Data);
        await registerDocument(payload);
        updateFileEntry(fileItem.id, { status: UPLOAD_STATUS.SUCCESS, progress: 0 });
        successCount++;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save document";
        updateFileEntry(fileItem.id, {
          status: UPLOAD_STATUS.FAILED,
          errorMessage,
        });
        failCount++;
      }
    }

    setIsRegistering(false);

    if (successCount > 0 && typeof onUploadComplete === "function") {
      onUploadComplete();
    }

    if (failCount === 0 && successCount > 0) {
      toast.success(
        `${successCount} document${successCount > 1 ? "s" : ""} uploaded successfully`
      );
      handleClear();
      handleClose(false);
    } else if (successCount > 0) {
      toast.warning(
        `${successCount} document${successCount > 1 ? "s" : ""} uploaded, ${failCount} failed`
      );
    } else {
      toast.error("All uploads failed. Please review the errors and try again.");
    }
  };

  const showBulkLimitError = (message) => {
    setSelectionLimitError(message);
    toast.error(message);
  };

  const addSelectedFiles = (filesToAdd) => {
    if (isUserGracePeriodOver || isRegistering) return;
    if (!filesToAdd?.length) return;

    const maxBulkFiles = DOCUMENT_CONSTANTS.MAX_BULK_UPLOAD_FILES;
    const currentCount = selectedFiles.length;
    const totalAfterSelection = currentCount + filesToAdd.length;

    if (currentCount >= maxBulkFiles) {
      showBulkLimitError(
        `Maximum ${maxBulkFiles} documents allowed per bulk upload. Remove a file before adding more.`
      );
      return;
    }

    if (filesToAdd.length > maxBulkFiles) {
      showBulkLimitError(
        `You selected ${filesToAdd.length} files. Maximum ${maxBulkFiles} documents allowed per bulk upload.`
      );
      return;
    }

    if (totalAfterSelection > maxBulkFiles) {
      showBulkLimitError(
        `Maximum ${maxBulkFiles} documents allowed per bulk upload. You already have ${currentCount} selected — choose ${maxBulkFiles - currentCount} or fewer.`
      );
      return;
    }

    setSelectionLimitError("");

    const filesQueue = filesToAdd;

    const maxSizeMB = DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB;
    const newEntries = [];

    for (const file of filesQueue) {
      const typeMessage = isValidFile(file, "any");
      if (typeMessage.length > 0) {
        toast.error(`${file.name}: ${typeMessage}`);
        continue;
      }

      const sizeMessage = maxFileSize(file, String(maxSizeMB));
      if (sizeMessage.length > 0) {
        toast.error(`${file.name}: ${sizeMessage}`);
        continue;
      }

      if (file.name.length > 150) {
        toast.error(`${file.name}: File name exceeds 150 characters`);
        continue;
      }

      const isDuplicate = selectedFiles.some(
        (entry) =>
          entry.file.name === file.name &&
          entry.status !== UPLOAD_STATUS.FAILED
      );
      if (isDuplicate) {
        toast.error(`${file.name} is already selected`);
        continue;
      }

      newEntries.push({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        s3Data: null,
        errorMessage: null,
      });
    }

    if (newEntries.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newEntries]);
      newEntries.forEach((entry) => startS3Upload(entry));
    }
  };

  const onDrop = (acceptedFiles) => {
    addSelectedFiles(acceptedFiles);
  };

  const bulkUploadLimit = DOCUMENT_CONSTANTS.MAX_BULK_UPLOAD_FILES;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUserGracePeriodOver || isRegistering,
    multiple: true,
  });

  const fileInputProps = getInputProps({ className: "hidden" });

  const dropzoneHintText =
    selectedFiles.length > 0
      ? `${selectedFiles.length} file${
          selectedFiles.length > 1 ? "s" : ""
        } selected — drag more here or click to add`
      : "Drag and drop files here, or click to browse";

  const removeSelectedFile = (id) => {
    const item = selectedFiles.find((f) => f.id === id);
    if (
      !item ||
      isRegistering ||
      item.status === UPLOAD_STATUS.UPLOADING ||
      item.status === UPLOAD_STATUS.REGISTERING
    ) {
      return;
    }
    uploadingRef.current.delete(id);
    setSelectedFiles((prev) => {
      const next = prev.filter((f) => f.id !== id);
      if (next.length < DOCUMENT_CONSTANTS.MAX_BULK_UPLOAD_FILES) {
        setSelectionLimitError("");
      }
      return next;
    });
  };

  const retryFailedUpload = (fileItem) => {
    if (isRegistering || isS3Uploading) return;
    const retryItem = {
      ...fileItem,
      status: UPLOAD_STATUS.PENDING,
      progress: 0,
      s3Data: null,
      errorMessage: null,
    };
    updateFileEntry(fileItem.id, retryItem);
    startS3Upload(retryItem);
  };

  const canRemoveFile = (item) =>
    !isRegistering &&
    item.status !== UPLOAD_STATUS.UPLOADING &&
    item.status !== UPLOAD_STATUS.REGISTERING &&
    item.status !== UPLOAD_STATUS.SUCCESS;

  const showProgressBar = (item) =>
    item.status === UPLOAD_STATUS.UPLOADING &&
    item.progress > 0 &&
    item.progress < 100;

  const canSubmit =
    !isUserGracePeriodOver &&
    !isRegistering &&
    !isS3Uploading &&
    hasReadyFiles &&
    (role === "sil" || selectedFileType);

  const modalFooter = () => (
    <>
      <CustomButton
        label='Cancel'
        variant='secondary'
        isDisabled={isRegistering}
        clickHandler={() => {
          if (isRegistering) return;
          handleClear();
          handleClose(false);
        }}
      />
      <CustomButton
        showLoading={isRegistering || isS3Uploading}
        isDisabled={!canSubmit}
        label={
          isS3Uploading || isRegistering
            ? "Uploading..."
            : "Upload"
        }
        variant='primary'
        clickHandler={handleRegisterDocuments}
      />
    </>
  );

  return (
    <ModalSkeleton
      title='Bulk Upload Documents'
      cssClass='w-[42%]'
      modalFooter={modalFooter()}
      isOpen={isOpen}
      closeModal={() => {
        if (isRegistering || isS3Uploading) return;
        handleClear();
        handleClose(false);
      }}
    >
      <div className='flex flex-col gap-4'>
        {role !== "sil" && (
          <div className='did-floating-label-content w-full'>
            <label className='text-start did-floating-label'>
              Document Category <span className='text-red-500'>*</span>
            </label>
            <Select
              isDisabled={isDocTypeLoader || isRegistering || isS3Uploading}
              loading={isDocTypeLoader}
              cssClass='z-20'
              isSearch={false}
              data={fileTypeOptions}
              selectedOption={selectedFileType}
              handleChange={(e) => setSelectedFileType(e)}
            />
          </div>
        )}

        {role === "participant" && (
          <div className='flex items-center gap-2'>
            <label className='text-start did-floating-label'>Show to Worker</label>
            <input
              type='checkbox'
              checked={!visibility}
              disabled={isRegistering || isS3Uploading}
              onChange={() => setVisibility((prev) => !prev)}
              className='rounded'
            />
          </div>
        )}

        <div>
          <label className='text-start did-floating-label mb-2 block'>
            Select Files
          </label>
          <div
            {...getRootProps()}
            className={classNames(
              "border-2 border-dashed rounded-lg px-6 py-6 text-center cursor-pointer transition-colors",
              isDragActive
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 bg-white hover:border-blue-300",
              (isUserGracePeriodOver || isRegistering) &&
                "opacity-50 cursor-not-allowed"
            )}
          >
            <input {...fileInputProps} />
            <p className='text-sm text-gray-700 font-medium'>{dropzoneHintText}</p>
            <p className='text-xs text-gray-500 mt-1'>
              Up to {bulkUploadLimit} files per bulk upload. Max{" "}
              {DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB}MB per file. Select files
              and click Upload when the progress bars complete.
            </p>
            {selectionLimitError ? (
              <p className='text-xs text-red-600 mt-2 font-medium'>
                {selectionLimitError}
              </p>
            ) : null}
          </div>

          {selectedFiles.length > 0 && (
            <div
              className='mt-3 flex flex-col gap-2 max-h-64 overflow-y-auto custom-scroll'
              onClick={(e) => e.stopPropagation()}
            >
              {selectedFiles.map((item) => (
                <div
                  key={item.id}
                  className='border rounded-lg border-gray-300 px-3 py-2 bg-white'
                >
                  <div className='flex items-center justify-between gap-3 text-sm min-w-0'>
                    <div className='flex min-w-0 flex-1 items-center gap-2'>
                      <div className='bg-blue-50 rounded-full h-8 w-8 flex-shrink-0 flex items-center justify-center'>
                        {FileIcon(getFileExtension(item.file.name), 16)}
                      </div>
                      <p
                        className='font-medium text-gray-900 truncate'
                        title={item.file.name}
                      >
                        {item.file.name}
                      </p>
                    </div>
                    <div className='flex items-center gap-2 flex-shrink-0'>
                      {item.status === UPLOAD_STATUS.FAILED && (
                        <button
                          type='button'
                          onClick={() => retryFailedUpload(item)}
                          disabled={isRegistering || isS3Uploading}
                          className='text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50'
                        >
                          Retry
                        </button>
                      )}
                      {canRemoveFile(item) && (
                        <button
                          type='button'
                          onClick={() => removeSelectedFile(item.id)}
                          disabled={isRegistering}
                          className='text-xs font-medium text-red-500 hover:text-red-700 disabled:opacity-50 whitespace-nowrap'
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {item.errorMessage && (
                    <p
                      className='text-xs text-red-500 mt-1 pl-10 truncate'
                      title={item.errorMessage}
                    >
                      {item.errorMessage}
                    </p>
                  )}
                  {showProgressBar(item) && (
                    <div className='mt-2 pl-10 pr-1'>
                      <div className='w-full bg-gray-200 rounded-full overflow-hidden'>
                        <div
                          className='animate-pulse transition duration-300 ease-in-out bg-purple-600 text-xs font-medium text-blue-100 text-center py-0.5 leading-none rounded-full'
                          style={{
                            width: `${Math.min(item.progress || 0, 100)}%`,
                          }}
                        >
                          {Math.round(item.progress || 0)}%
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalSkeleton>
  );
};

const mapStateToProps = (state) => {
  const currentLoginUser = getUser(state);
  return { currentLoginUser };
};

export default connect(mapStateToProps, null)(BulkUploadDocumentModal);
